import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LANGUAGE_KEY = 'mithaq_lang';

/** All supported languages */
export const SUPPORTED_LANGUAGES = [
  { code: 'ar', label: 'العربية',  nativeLabel: 'العربية',  dir: 'rtl', flag: '🇸🇦' },
  { code: 'en', label: 'English',  nativeLabel: 'English',   dir: 'ltr', flag: '🇬🇧' },
  { code: 'ur', label: 'اردو',     nativeLabel: 'اردو',      dir: 'rtl', flag: '🇵🇰' },
  { code: 'fr', label: 'Français', nativeLabel: 'Français',  dir: 'ltr', flag: '🇫🇷' },
  { code: 'zh', label: '中文',     nativeLabel: '中文',      dir: 'ltr', flag: '🇨🇳' },
];

/** Languages that use right-to-left layout */
export const RTL_LANGUAGES = new Set(['ar', 'ur']);

export function isRtl(lang) {
  return RTL_LANGUAGES.has(lang);
}

const VALID_CODES = new Set(SUPPORTED_LANGUAGES.map(l => l.code));

function getInitialLanguage() {
  const saved = localStorage.getItem(LANGUAGE_KEY);
  return VALID_CODES.has(saved) ? saved : 'ar';
}

const LanguageContext = createContext({
  language:       'ar',
  direction:      'rtl',
  setLanguage:    () => {},
  toggleLanguage: () => {},
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((next) => {
    const lang = VALID_CODES.has(next) ? next : 'ar';
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_KEY, lang);
  }, []);

  /** Legacy toggle: ar ↔ en (kept for backward compat where toggleLanguage is still used) */
  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  }, [language, setLanguage]);

  const direction = isRtl(language) ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
  }, [language, direction]);

  const value = useMemo(
    () => ({ language, direction, setLanguage, toggleLanguage }),
    [language, direction, setLanguage, toggleLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
